<template>
  <div v-if="products && products.length > 0" class="product-grid">
    <sky v-if="!cloudsDisabled" :columns="3" />
    <marquee
      v-if="!hideTopMarquee"
      always
      class="product-grid__marquee product-grid__marquee--top"
    >
      <div
        v-if="marquees && marquees.length > 0"
        class="product-grid__marquee-text"
      >
        <span
          v-for="(text, i) in marquees"
          :key="`marquee-text-top-${i}`"
          class="inline-block"
          >{{ text }}&nbsp;{{ i % 2 ? '↑' : '←' }}&nbsp;</span
        >
      </div>
      <div v-else class="product-grid__marquee-text">
        <span
          v-for="(text, i) in slogans"
          :key="`marquee-text-top-${i}`"
          class="inline-block"
          >{{ text }}&nbsp;{{ i % 2 ? '↑' : '←' }}&nbsp;</span
        >
      </div>
    </marquee>
    <slot name="header" />
    <div
      class="product-grid__grid"
      :style="{
        '--product-grid-item-border-vertical-height': `${PRODUCT_GRID_ITEM_BORDER_VERTICAL_WIDTH}px`,
        'grid-template-columns': colCount
          ? `repeat(${colCount}, 1fr)`
          : undefined
      }"
    >
      <div
        v-for="(product, i) in filterVisibleProducts(products)"
        :key="product._id"
        ref="tracks"
        class="product-grid__track"
        :class="{
          'product-grid__track--collapsed':
            isCollapsed && i + 1 > cellCountMaxVisible,
          'product-grid__track--active': activeCell === i
        }"
      >
        <product-grid-item
          :product="product"
          :index="i"
          :total-cols="colCountInternal"
          :active="activeCell === i"
          class="product-grid__cell"
          @over="over"
          @out="out"
        >
          <i
            v-for="side in 4"
            :key="`product-cell-${side}-edge-${side}`"
            class="product-grid__edge"
            aria-hidden="true"
          />
        </product-grid-item>
      </div>
      <div
        v-for="i in emptyTracks"
        :key="`track-${i}`"
        class="product-grid__track"
        :class="{
          'product-grid__track--collapsed': isCollapsed
        }"
      >
        <div class="product-grid__cell" aria-hidden="true">
          <i
            v-for="j in 4"
            :key="`empty-cell-${i}-edge-${j}`"
            class="product-grid__edge"
          />
        </div>
      </div>
    </div>

    <section
      v-if="isShowingSeeMore"
      class="flex justify-center p-8"
      @click="showMore"
    >
      <FormButton
        :background-color="buttonBackgroundColor"
        class="product-grid__show-more-button"
      >
        {{ showMoreText }}
      </FormButton>
    </section>

    <marquee
      v-if="!hideBottomMarquee"
      always
      reverse
      class="product-grid__marquee product-grid__marquee--bottom"
    >
      <div
        v-if="marquees && marquees.length > 0"
        class="product-grid__marquee-text"
      >
        <span
          v-for="(text, i) in marquees"
          :key="`marquee-text-top-${i}`"
          class="inline-block"
          >{{ text }}&nbsp;{{ i % 2 ? '↑' : '←' }}&nbsp;</span
        >
      </div>
      <div v-else class="product-grid__marquee-text">
        <span
          v-for="(text, i) in slogans"
          :key="`marquee-text-bottom-${i}`"
          class="inline-block"
          >{{ text }}&nbsp;{{ i % 2 ? '↑' : '←' }}&nbsp;</span
        >
      </div>
    </marquee>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import FormButton from '../../form/button.vue'
import ProductGridItem from './product-grid-item.vue'
import { slogans, colors } from '~/recess.json'

const PRODUCT_GRID_ITEM_BORDER_VERTICAL_WIDTH = 1

export default {
  name: 'ProductGrid',

  components: {
    ProductGridItem,
    FormButton
  },

  props: {
    products: {
      default: () => [],
      type: Array
    },
    marquees: {
      default: undefined,
      type: Array
    },
    collapsible: {
      default: false,
      type: Boolean
    },
    colCount: {
      default: undefined,
      type: Number
    },
    hideBottomMarquee: {
      default: false,
      type: Boolean
    },
    hideTopMarquee: {
      default: false,
      type: Boolean
    },
    buttonBackgroundColor: {
      default: colors.apricot,
      type: String
    },
    backgroundColor: {
      default: colors.apricot,
      type: String
    },
    cloudsDisabled: {
      default: false,
      type: Boolean
    },
    showMoreText: {
      type: String,
      default: 'show more flavors'
    }
  },

  data: component => ({
    trackHeight: 0,
    rowCount: 0,
    isCollapsed: component.collapsible,
    colCountInternal: 0,
    emptyTracks: 0,
    cloudsWidth: 0,
    cloudsSpeed: 12,
    cloudsDistance: 4,
    activeCell: null,
    PRODUCT_GRID_ITEM_BORDER_VERTICAL_WIDTH,
    slogans
  }),

  computed: {
    ...mapGetters('screen', {
      bp: 'breakpoint'
    }),
    isShowingSeeMore() {
      return this.collapsible && this.isCollapsed && this.rowCount > 1
    },
    isShowingClouds() {
      return this.bp && !this.cloudsDisabled
    },
    cellCountMaxVisible() {
      return this.isCollapsed ? this.colCountInternal : Infinity
    }
  },

  mounted() {
    this.$nextTick(this.resize)
    this.$bus.$on('resize', this.resize)
  },

  beforeDestroy() {
    this.$bus.$off('resize', this.resize)
  },

  methods: {
    async setup() {
      // force repaint to ensure correct sizes
      await new Promise(resolve => requestAnimationFrame(resolve))
      const tracks = this.$refs.tracks
      if (!tracks || !tracks.length) {
        return
      }
      const hasColCountDefined = this.colCount !== undefined
      const totalTracks = tracks.length
      const track = tracks[0]
      const trackHeight =
        track.clientHeight - PRODUCT_GRID_ITEM_BORDER_VERTICAL_WIDTH
      const width = this.$el.clientWidth
      const trackWidth = track.clientWidth - 1

      const colCountInternal = hasColCountDefined
        ? this.colCount
        : Math.max(1, Math.floor(width / trackWidth))

      const rowCount = Math.ceil(totalTracks / colCountInternal)

      const remainder = rowCount * colCountInternal - tracks.length

      this.rowCount = rowCount
      this.trackHeight = trackHeight
      this.colCountInternal = colCountInternal
      this.cloudsWidth = trackWidth
      this.emptyTracks = Math.max(0, remainder)
      this.isCollapsed = this.rowCount > 1 && this.collapsible
    },
    over(index) {
      this.activeCell = index
    },
    out() {
      this.activeCell = null
    },
    resize() {
      this.emptyTracks = 0
      this.$nextTick(this.setup)
    },
    showMore() {
      this.isCollapsed = false
    },
    filterVisibleProducts(products) {
      return products.filter(product => !product.hideFromCollection)
    }
  }
}
</script>

<style>
.product-grid {
  --grid-text-color: theme('colors.default-text-color');
  color: var(--grid-text-color);
  transition: height 200ms ease-out;
}

.product-grid .product-grid__show-more-button {
  text-transform: lowercase;
}

.product-grid__cell {
  color: var(--grid-text-color);
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  padding: var(--grid-track-gutter);
  position: relative;
  text-align: center;
}

.is-scrolling .product-grid__cell {
  pointer-events: none;
}

.product-grid__marquee {
  border-left: var(--border-vertical-width) solid var(--grid-text-color);
  border-right: var(--border-vertical-width) solid var(--grid-text-color);
  color: var(--grid-text-color);
  font-family: theme('fontFamily.antidote');
  line-height: 1;
}

.product-grid__marquee--top {
  margin-bottom: -1px;
}

.product-grid__marquee--bottom {
  margin-top: -1px;
}

.product-grid__marquee-text {
  border-bottom: 2px solid var(--grid-text-color);
  border-top: 2px solid var(--grid-text-color);
  padding: 0.75em 0;
  white-space: nowrap;
}

.product-grid__grid {
  display: grid;
  grid-auto-rows: 1fr;
  grid-gap: 0;
  grid-template-columns: repeat(
    auto-fit,
    minmax(var(--grid-track-min), var(--grid-track-max))
  );
  grid-template-rows: repeat(
    auto-fit,
    minmax(var(--grid-track-min), var(--grid-track-max))
  );
  height: var(--grid-height);
}

.product-grid__track {
  position: relative;
}

.product-grid__track--collapsed {
  display: none;
}

.product-grid__track::before {
  content: '';
  display: block;
  height: 0;
}

.product-grid__edge {
  background-color: var(--grid-text-color);
  content: '';
  display: none;
  position: absolute;
  z-index: 3;
}

/* top edge */
.product-grid__edge:nth-child(1) {
  display: block;
  height: 2px;
  left: 0;
  top: -1px;
  width: 100%;
}

/* right edge */
.product-grid__edge:nth-child(2) {
  height: 100%;
  right: -1px;
  top: 0;
  width: 2px;
}

/* bottom edge */
.product-grid__edge:nth-child(3) {
  bottom: -1px;
  display: block;
  height: 2px;
  left: 0;
  width: 100%;
}

/* left edge */
.product-grid__edge:nth-child(4) {
  height: 100%;
  left: -1px;
  top: 0;
  width: 2px;
}

.product-grid__clouds,
.product-grid__cloud {
  left: 0;
  position: absolute;
  width: 100%;
}

.product-grid__clouds {
  height: 100%;
  overflow: hidden;
  top: 0;
}

.product-grid__cloud.drifting-cloud {
  --column-count: 8;
}

.product-grid__cloud--empty.drifting-cloud {
  --column-count: 3;
}

@screen xs {
  .product-grid__edge {
    display: block;
  }
}
</style>
